import type { LoadEffect, LoadTableId } from "../domain/types/index.js";

export const LOAD_EFFECTS = {
  "unwanted-staff": { id: "unwanted-staff", name: "応援人員の押し付け", weight: 0, addTemporaryCards: { cardId: "idle", count: 2 } },
  "pending-tasks": { id: "pending-tasks", name: "差し戻しの山", weight: 0, addTemporaryCards: { cardId: "unresolved-task", count: 2 } },
  "draw-restriction": { id: "draw-restriction", name: "情報共有の制限", weight: 0, nextActionDrawLimit: 1 },
  interrupt: { id: "interrupt", name: "割り込み", weight: 0, fatigue: 1 },
  "weekend-work": { id: "weekend-work", name: "休日出勤", weight: 0, fatigue: 2 },
  "incident-response": { id: "incident-response", name: "障害対応", weight: 0, fatigue: 2 },
  "update-eve": { id: "update-eve", name: "アップデート前夜", weight: 0, fatigue: 3 },
  "night-release": { id: "night-release", name: "夜間リリース", weight: 0, fatigue: 4 },
  "service-outage": { id: "service-outage", name: "サービス停止", weight: 0, fatigue: 5 },
  "system-outage": { id: "system-outage", name: "システム障害", weight: 0, nextHandDiscard: 2 },
  "critical-system-outage": { id: "critical-system-outage", name: "深刻なシステム障害", weight: 0, nextHandDiscard: 3 },
  "rework-ticket": { id: "rework-ticket", name: "手戻りチケット", weight: 0, progress: -1 },
  sendback: { id: "sendback", name: "差し戻し", weight: 0, progress: -2 },
  rebuild: { id: "rebuild", name: "作り直し", weight: 0, progress: -3 },
  "req-change": { id: "req-change", name: "要件変更", weight: 0, progress: -4 },
  scrap: { id: "scrap", name: "白紙撤回", weight: 0, progress: -5 },
  "expectation-gap": { id: "expectation-gap", name: "認識ズレ", weight: 0, fatigue: 1, progress: -1 },
  "client-review": { id: "client-review", name: "顧客レビュー", weight: 0, fatigue: 2, progress: -2 },
  "scope-creep": { id: "scope-creep", name: "仕様追加", weight: 0, fatigue: 3, progress: -3 },
  "prod-incident": { id: "prod-incident", name: "本番障害", weight: 0, fatigue: 4, progress: -4 },
} as const satisfies Record<string, Omit<LoadEffect, "weight"> & { weight: number }>;

type LoadEntry = { ref: keyof typeof LOAD_EFFECTS; weight: number };

function buildTable(entries: readonly LoadEntry[]): LoadEffect[] {
  return entries.map(({ ref, weight }) => ({ ...LOAD_EFFECTS[ref], weight }));
}

export const LOAD_TABLES = {
  // 第15期。プロジェクトごとの性質を混ぜすぎず、マップ上で相性を判断できるようにする。
  "foresight-rollback": buildTable([{ ref: "sendback", weight: 80 }, { ref: "rebuild", weight: 20 }]),
  "foresight-rebuild": buildTable([{ ref: "rebuild", weight: 60 }, { ref: "req-change", weight: 20 }, { ref: "scrap", weight: 20 }]),
  "foresight-fatigue-intro": buildTable([{ ref: "weekend-work", weight: 80 }, { ref: "update-eve", weight: 20 }]),
  "foresight-fatigue": buildTable([{ ref: "weekend-work", weight: 30 }, { ref: "update-eve", weight: 40 }, { ref: "night-release", weight: 30 }]),
  "foresight-hand": buildTable([{ ref: "system-outage", weight: 70 }, { ref: "weekend-work", weight: 30 }]),
  "foresight-hand-hard": buildTable([{ ref: "critical-system-outage", weight: 45 }, { ref: "system-outage", weight: 55 }]),
  "foresight-staff": buildTable([{ ref: "unwanted-staff", weight: 50 }, { ref: "weekend-work", weight: 30 }, { ref: "update-eve", weight: 20 }]),
  "foresight-tasks": buildTable([{ ref: "pending-tasks", weight: 45 }, { ref: "weekend-work", weight: 55 }]),
  "foresight-draw": buildTable([{ ref: "draw-restriction", weight: 75 }, { ref: "sendback", weight: 25 }]),
  "foresight-boss": buildTable([{ ref: "pending-tasks", weight: 40 }, { ref: "draw-restriction", weight: 30 }, { ref: "update-eve", weight: 30 }]),
  // 第16期。停止・滞留・現場疲労を、プロジェクトごとに判別できる輪郭で構成する。
  "crisis-outage": buildTable([{ ref: "system-outage", weight: 70 }, { ref: "sendback", weight: 30 }]),
  "crisis-blackout": buildTable([{ ref: "critical-system-outage", weight: 55 }, { ref: "req-change", weight: 45 }]),
  "crisis-backlog": buildTable([{ ref: "pending-tasks", weight: 55 }, { ref: "sendback", weight: 25 }, { ref: "weekend-work", weight: 20 }]),
  "crisis-pressure": buildTable([{ ref: "unwanted-staff", weight: 70 }, { ref: "weekend-work", weight: 20 }, { ref: "update-eve", weight: 10 }]),
  "crisis-fatigue": buildTable([{ ref: "update-eve", weight: 55 }, { ref: "night-release", weight: 45 }]),
  "crisis-boss": buildTable([{ ref: "system-outage", weight: 30 }, { ref: "pending-tasks", weight: 35 }, { ref: "update-eve", weight: 35 }]),
  // 第17期の序盤は、対策札を獲得する前に詰ませず、攻撃の種類を読んで進路を選ばせる。
  "inheritance-hand": buildTable([{ ref: "system-outage", weight: 30 }, { ref: "rework-ticket", weight: 40 }, { ref: "interrupt", weight: 30 }]),
  "inheritance-backlog": buildTable([{ ref: "pending-tasks", weight: 30 }, { ref: "rework-ticket", weight: 40 }, { ref: "interrupt", weight: 30 }]),
  "inheritance-pressure": buildTable([{ ref: "unwanted-staff", weight: 40 }, { ref: "interrupt", weight: 40 }, { ref: "rework-ticket", weight: 20 }]),
  // 第18期。採用過多で起きる手札の偏り、施策連鎖の停滞、拡大期の滞留を分けて提示する。
  "graduate-intake": buildTable([{ ref: "unwanted-staff", weight: 45 }, { ref: "interrupt", weight: 35 }, { ref: "rework-ticket", weight: 20 }]),
  "graduate-orientation": buildTable([{ ref: "draw-restriction", weight: 40 }, { ref: "pending-tasks", weight: 25 }, { ref: "rework-ticket", weight: 35 }]),
  "graduate-scale": buildTable([{ ref: "unwanted-staff", weight: 35 }, { ref: "pending-tasks", weight: 35 }, { ref: "weekend-work", weight: 30 }]),
  "graduate-boss": buildTable([{ ref: "draw-restriction", weight: 30 }, { ref: "pending-tasks", weight: 35 }, { ref: "update-eve", weight: 35 }]),
  // 第19期。窓際の押し付けを、変換・再起用・抱え込みの判断へ変える。
  "window-seat": buildTable([{ ref: "unwanted-staff", weight: 50 }, { ref: "interrupt", weight: 30 }, { ref: "rework-ticket", weight: 20 }]),
  "window-rotation": buildTable([{ ref: "unwanted-staff", weight: 45 }, { ref: "draw-restriction", weight: 30 }, { ref: "rework-ticket", weight: 25 }]),
  "window-backlog": buildTable([{ ref: "pending-tasks", weight: 40 }, { ref: "unwanted-staff", weight: 35 }, { ref: "rework-ticket", weight: 25 }]),
  "window-boss": buildTable([{ ref: "unwanted-staff", weight: 35 }, { ref: "pending-tasks", weight: 30 }, { ref: "draw-restriction", weight: 20 }, { ref: "update-eve", weight: 15 }]),
  // 第20期。投資施策ごとに「どの負荷へ先回りするか」が読めるよう分ける。
  "investment-fatigue": buildTable([
    { ref: "interrupt", weight: 45 }, { ref: "weekend-work", weight: 35 }, { ref: "expectation-gap", weight: 20 },
  ]),
  "investment-backlog": buildTable([
    { ref: "unwanted-staff", weight: 45 }, { ref: "pending-tasks", weight: 35 }, { ref: "draw-restriction", weight: 20 },
  ]),
  "investment-rollback": buildTable([
    { ref: "rework-ticket", weight: 45 }, { ref: "sendback", weight: 30 }, { ref: "system-outage", weight: 25 },
  ]),
  "investment-squeeze": buildTable([
    { ref: "sendback", weight: 30 }, { ref: "client-review", weight: 25 }, { ref: "weekend-work", weight: 25 }, { ref: "system-outage", weight: 20 },
  ]),
  // 試作用の混合表。第15期の実プロジェクトは、上の性質別テーブルを使う。
  "disruption-prototype": buildTable([
    { ref: "unwanted-staff", weight: 30 },
    { ref: "pending-tasks", weight: 30 },
    { ref: "draw-restriction", weight: 40 },
  ]),
  intro: buildTable([
    { ref: "interrupt", weight: 50 },
    { ref: "rework-ticket", weight: 50 },
  ]),
  small: buildTable([
    { ref: "interrupt", weight: 40 },
    { ref: "weekend-work", weight: 30 },
    { ref: "expectation-gap", weight: 30 },
  ]),
  "small-mix": buildTable([
    { ref: "interrupt", weight: 40 },
    { ref: "weekend-work", weight: 30 },
    { ref: "sendback", weight: 30 },
  ]),
  medium: buildTable([
    { ref: "weekend-work", weight: 40 },
    { ref: "sendback", weight: 30 },
    { ref: "client-review", weight: 30 },
  ]),
  standard: buildTable([
    { ref: "interrupt", weight: 60 },
    { ref: "sendback", weight: 25 },
    { ref: "rework-ticket", weight: 15 },
  ]),
  volatile: buildTable([
    { ref: "interrupt", weight: 30 },
    { ref: "sendback", weight: 35 },
    { ref: "rework-ticket", weight: 20 },
    { ref: "system-outage", weight: 15 },
  ]),
  incident: buildTable([
    { ref: "interrupt", weight: 40 },
    { ref: "weekend-work", weight: 25 },
    { ref: "incident-response", weight: 35 },
  ]),
  depth: buildTable([
    { ref: "sendback", weight: 30 },
    { ref: "rebuild", weight: 30 },
    { ref: "client-review", weight: 40 },
  ]),
  climax: buildTable([
    { ref: "rebuild", weight: 25 },
    { ref: "scope-creep", weight: 25 },
    { ref: "update-eve", weight: 25 },
    { ref: "req-change", weight: 25 },
  ]),
  large: buildTable([
    { ref: "weekend-work", weight: 20 },
    { ref: "sendback", weight: 20 },
    { ref: "update-eve", weight: 20 },
    { ref: "rebuild", weight: 20 },
    { ref: "scope-creep", weight: 20 },
  ]),
  boss: buildTable([
    { ref: "night-release", weight: 16 },
    { ref: "service-outage", weight: 16 },
    { ref: "req-change", weight: 16 },
    { ref: "scrap", weight: 16 },
    { ref: "prod-incident", weight: 16 },
    { ref: "critical-system-outage", weight: 20 },
  ]),
} as const satisfies Record<LoadTableId, readonly LoadEffect[]>;
